export type FlexWindowMinutes = {
  startMinutes: number
  endMinutes: number
}

export type FlexSlotUnavailableReason = "outside_working_time" | "blocked_by_absence_or_blocker"

export type FlexSlotFrame = {
  startMinutes: number
  endMinutes: number
  blockedRatio: number
  blockedMinutes: number
  nonWorkingMinutes: number
  blockerMinutes: number
  available: boolean
  reasonCode: FlexSlotUnavailableReason | null
}

type BuildFlexSlotFramesInput = {
  workWindows: FlexWindowMinutes[]
  blockerWindows?: FlexWindowMinutes[]
  flexSlotsPerDay: number | null | undefined
  stepMinutes?: number
}

export const resolveFlexSlotCount = (
  workWindows: FlexWindowMinutes[],
  flexSlotsPerDay: number | null | undefined,
) => {
  const configured = Number(flexSlotsPerDay ?? 0)

  if (!Number.isFinite(configured) || configured <= 0) return 0

  const windows = mergeWindows(workWindows)

  if (!windows.length) return 0

  const outerStart = Math.min(...windows.map((window) => window.startMinutes))
  const outerEnd = Math.max(...windows.map((window) => window.endMinutes))
  const outerMinutes = Math.max(0, outerEnd - outerStart)

  if (outerMinutes <= 0) return 0

  return Math.max(0, Math.floor((configured * outerMinutes) / 480))
}

export const buildFlexSlotFrames = ({
  workWindows,
  blockerWindows = [],
  flexSlotsPerDay,
  stepMinutes = 5,
}: BuildFlexSlotFramesInput): FlexSlotFrame[] => {
  const windows = mergeWindows(workWindows)

  if (!windows.length) return []

  const outerStart = Math.min(...windows.map((window) => window.startMinutes))
  const outerEnd = Math.max(...windows.map((window) => window.endMinutes))
  const count = resolveFlexSlotCount(windows, flexSlotsPerDay)

  if (count <= 0) return []

  const nonWorkingWindows = complementWindows(windows, outerStart, outerEnd)
  const normalizedBlockers = mergeWindows(
    blockerWindows
      .map((window) => clampWindow(window, outerStart, outerEnd))
      .filter((window): window is FlexWindowMinutes => Boolean(window)),
  )
  const unavailableWindows = mergeWindows([...nonWorkingWindows, ...normalizedBlockers])
  const outerMinutes = Math.max(0, outerEnd - outerStart)
  const segment = outerMinutes / count
  const slots: FlexSlotFrame[] = []
  let cursor = outerStart
  for (let index = 0; index < count; index += 1) {
    const rawStart = cursor
    const rawEnd = index === count - 1 ? outerEnd : outerStart + segment * (index + 1)
    const startMinutes = snapToStep(rawStart, stepMinutes, "ceil")
    const endMinutes = snapToStep(rawEnd, stepMinutes, "floor")
    cursor = endMinutes

    if (endMinutes <= startMinutes) continue

    const duration = endMinutes - startMinutes
    const blockedMinutes = overlapMinutes(unavailableWindows, startMinutes, endMinutes)
    const nonWorkingMinutes = overlapMinutes(nonWorkingWindows, startMinutes, endMinutes)
    const blockerMinutes = overlapMinutes(normalizedBlockers, startMinutes, endMinutes)
    const blockedRatio = duration > 0 ? blockedMinutes / duration : 1
    const available = blockedRatio <= 0.5
    slots.push({
      startMinutes,
      endMinutes,
      blockedRatio,
      blockedMinutes,
      nonWorkingMinutes,
      blockerMinutes,
      available,
      reasonCode: available
        ? null
        : nonWorkingMinutes > blockerMinutes
          ? "outside_working_time"
          : "blocked_by_absence_or_blocker",
    })
  }

  return slots
}

const snapToStep = (value: number, stepMinutes: number, mode: "ceil" | "floor") => {
  const step = Math.max(1, Math.round(Number(stepMinutes || 1)))

  if (mode === "floor") return Math.floor(value / step) * step

  return Math.ceil(value / step) * step
}

const mergeWindows = (windows: FlexWindowMinutes[]): FlexWindowMinutes[] => {
  const normalized = windows
    .map((window) => ({
      startMinutes: Number(window.startMinutes),
      endMinutes: Number(window.endMinutes),
    }))
    .filter(
      (window) =>
        Number.isFinite(window.startMinutes) &&
        Number.isFinite(window.endMinutes) &&
        window.endMinutes > window.startMinutes,
    )
    .sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes)

  if (!normalized.length) return []

  const merged: FlexWindowMinutes[] = [normalized[0]]
  for (const window of normalized.slice(1)) {
    const current = merged[merged.length - 1]

    if (window.startMinutes <= current.endMinutes) {
      current.endMinutes = Math.max(current.endMinutes, window.endMinutes)
    } else {
      merged.push({ ...window })
    }
  }

  return merged
}

const complementWindows = (windows: FlexWindowMinutes[], outerStart: number, outerEnd: number) => {
  const result: FlexWindowMinutes[] = []
  let cursor = outerStart
  for (const window of windows) {
    if (window.startMinutes > cursor) {
      result.push({ startMinutes: cursor, endMinutes: window.startMinutes })
    }

    cursor = Math.max(cursor, window.endMinutes)
  }

  if (cursor < outerEnd) {
    result.push({ startMinutes: cursor, endMinutes: outerEnd })
  }

  return result
}

const clampWindow = (window: FlexWindowMinutes, outerStart: number, outerEnd: number) => {
  const startMinutes = Math.max(window.startMinutes, outerStart)
  const endMinutes = Math.min(window.endMinutes, outerEnd)

  if (endMinutes <= startMinutes) return null

  return { startMinutes, endMinutes }
}

const overlapMinutes = (windows: FlexWindowMinutes[], startMinutes: number, endMinutes: number) =>
  windows.reduce((total, window) => {
    const overlapStart = Math.max(startMinutes, window.startMinutes)
    const overlapEnd = Math.min(endMinutes, window.endMinutes)

    return overlapEnd > overlapStart ? total + (overlapEnd - overlapStart) : total
  }, 0)
